<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>

        <div
            v-if="initialLoading"
            key="initialLoading"
            class="animate__animated animate__fadeIn animate_fadeOut animate__fast"
        >
            <busy-spinner inner-class="my-5" text="Loading Users..."></busy-spinner>
        </div>

        <div 
            v-show="!initialLoading"
            key="initialLoadingDone"
        >  <!-- made it v-show instead of v-if so its in the DOM to handle preselectedIds before initialLoading is done -->
            
            <div class="d-flex justify-content-between mb-2">
                <div></div>
                <div>
                    <b-button variant="sharp-success" @click="handleAdd">
                        <b-icon icon="person-plus" class="mr-1"></b-icon>
                        Add New User
                    </b-button>
                </div>
            </div>
            
            <b-card 
                id="vendor-contact-table-card"
                class="rounded-lg animate__animated animate__fadeIn animate__fast"
                :class="noShadow ? '' : 'shadow'"
                no-body
            >
                <b-table 
                    ref="companyTuTable"
                    :items="itemList"
                    :fields="fieldsToShow"
                    :busy="loading"
                    primary-key="id" 
                    hover
                    class="no-top-border td-align-middle mb-0"
                    thead-class="overflow-hidden cells-align-middle"
                    head-variant="light"
                    :selectable="isTableSelectable"
                    :select-mode="tableSelectMode"
                    no-select-on-click
                    @row-selected="handleRowSelected"
                >
                    <!-- <template v-slot:table-busy>
                        <div class="text-center text-primary my-2">
                            <busy-spinner text="Loading..."></busy-spinner>
                        </div>
                    </template> -->
                    <template #cell(selectColumn)="data">
                        <span v-if="data.rowSelected && alwaysSelectedIds.includes(data.item.id)" key="alwaysSelected" class="font-size-70 font-weight-500 text-muted text-uppercase">
                            <!-- <b-icon icon="check-square-fill" variant="secondary" font-scale="1.5"></b-icon> -->
                            Required
                        </span>
                        <b-button v-else-if="data.rowSelected" key="isSelected" block variant="bare-secondary" size="sm" @click="data.unselectRow()">
                            <b-icon icon="check-square-fill" variant="primary" font-scale="1.5"></b-icon>
                        </b-button>
                        <b-button v-else key="isUnselected" block variant="bare-secondary" size="sm" @click="data.selectRow()">
                            <b-icon icon="square" variant="secondary" font-scale="1.5"></b-icon>
                        </b-button>
                    </template>
                    <template v-slot:cell(full_name)="data">
                        <div class="d-flex gap-3 align-items-center">
                            <!-- <b-icon 
                                v-if="isTableSelectable"
                                :icon="data.rowSelected ? 'check-square-fill' : 'square'" 
                                :variant="data.rowSelected ? 'primary' : 'secondary'" 
                                font-scale="1.2"
                            ></b-icon> -->
                            <div class="">
                                <b-link v-if="data.item.id !== currentUserInfo.tenant_user_id" @click="handleUpdate(data.item)">{{ data.value }}</b-link>
                                <span v-else>{{ data.value }}</span>
                                <b-badge
                                    v-if="data.item.role_slug === 'external_admin'"
                                    variant="soft-warning"
                                    class="text-uppercase ml-2"
                                >
                                    Admin
                                </b-badge>
                                <base-popover-icon 
                                    v-if="data.item.manual_block" 
                                    icon="exclamation-circle-fill" 
                                    icon-variant="danger" 
                                    title="Account On Hold"
                                    class="ml-2"
                                >
                                    This user's account has been placed on hold by Supplios.  Please contact Supplios support to remove this restriction.
                                </base-popover-icon>
                            </div>
                        </div>
                    </template>
                    <template #cell(company_name)="data">
                        <a v-if="data.item.company_url" :href="data.item.company_url">{{ data.value }}</a>
                        <span v-else>{{ data.value }}</span>
                    </template>
                    <template #cell(tenant_roles)="data">
                        <div class="d-flex gap-2 flex-wrap">
                            <b-badge 
                                v-for="role in (data.value || [])"
                                :key="role.slug"
                                variant="soft-secondary" 
                                class="text-uppercase"
                            >
                                {{ role.name }}
                            </b-badge>
                        </div>
                    </template>
                    <template #cell(portal_access)="data">
                        <b-badge
                            v-if="data.item.portalAccessParams"
                            :key="data.item.portalAccessParams.key"
                            :variant="data.item.portalAccessParams.variant"
                            class="text-uppercase"
                        >
                            {{ data.item.portalAccessParams.label }}
                        </b-badge>
                        <!-- <b-icon v-if="data.item.can_be_invited" icon="circle-fill" variant="success"></b-icon> -->
                    </template>
                    <template #cell(created)="data">
                        {{ data.value | dateFilter }}
                    </template>
                    <template #cell(actions)="data">
                        <b-dropdown variant="action-round" no-caret size="sm" toggle-class="">
                            <template #button-content>
                                <b-icon icon="three-dots"></b-icon>
                            </template>
                            <b-dropdown-item
                                :disabled="data.item.id === currentUserInfo.tenant_user_id"
                                v-if="canSendReminder(data.item)"
                                @click="sendReminder(data.item)"
                            >
                                <b-icon icon="envelope" variant="primary" class="mr-2"></b-icon>
                                {{ data.item.portal_access === 'expired_invite' ? 'Renew Invite' : 'Send Invite' }}
                            </b-dropdown-item>
                            <b-dropdown-item
                                :disabled="data.item.id === currentUserInfo.tenant_user_id || data.item.portal_access === 'on_hold'"
                                @click="removeAccess(data.item)"
                            >
                                <b-icon icon="x-circle" variant="info" class="mr-2"></b-icon>
                                Remove Access
                            </b-dropdown-item>
                            <b-dropdown-item
                                :disabled="data.item.id === currentUserInfo.tenant_user_id || data.item.portal_access === 'on_hold'"
                                @click="deactivateUser(data.item)"
                            >
                                <b-icon icon="trash" variant="danger" class="mr-2"></b-icon>
                                Delete User
                            </b-dropdown-item>
                        </b-dropdown>
                    </template>
                </b-table>
            </b-card>

        </div>  <!-- End of area to hide when loading -->

        <company-portal-user-form
            ref="companyUserForm"
            @save-complete="handleSaveComplete"
        ></company-portal-user-form>

        <b-modal
            v-model="messageModal"
            centered
            size="sm"
            hide-footer
            hide-header
        >
            <h5 class="text-center mb-0">{{ message }}</h5>
        </b-modal>

    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
//import BaseTablePagination from '../BaseTablePagination';
import { FormatDateHelper } from '../../js/filters-helpers';
//import ContactCreateUpdateModal from '../ContactCreateUpdateModal';  // using this only for updates, not creates
//import isEqual from 'lodash/isEqual';
import { getPortalAccessParams } from '@/components/tenant_users/tenantUserUtils';
import { currentUserInfoMixin, tenantInfoMixin } from '../../js/custom-vue-mixins';

// DON'T USE PAGINATION HERE 
// because we use it as a selector various places
// and we need full list to be loaded at once

export default {
    name: 'CompanyPortalUserList',
    components: {
        //BaseTablePagination,
        //ContactCreateUpdateModal,
    },
    mixins: [currentUserInfoMixin, tenantInfoMixin],
    props: {
        companyId: {  // if you pass in the company ID, it will only return contacts for that company
            // this allows you to re-use this component for Company Contact List also!
            type: [String, Number],
            required: false,
        },
        hideFields: {  // array of strings with field keys to HIDE (if they were to be shown normally)
            type: Array,
            required: false,
        },
        selectable: { // options are '' (off, default), 'multi', 'single', 'range'
            // we use this to set 'selectable' and 'select-mode' props on b-table
            // we use this in computed, transform to selectable (bool) and select-mode for b-table
            type: String,
            default: '',
            validator: function (value) {
                const opts = ['', 'multi', 'single'];  // 'range' could also work, but not with preselectedIds -- would have to rework things to make it work
                return opts.includes(value);
            },
        },
        preselectedIds: {
            // IDs (not full objects) of the items that should be PREselected
            // only used on FIRST LOAD, no watcher...
            // if you want something to be reactive, implement a different prop for that and put a watcher on it
            // things in itemList must have 'id' property -- that's what gets used
            // any EXTRA ids in here but not in the grid will just be ignored
            type: Array,
            default: () => [],
        },
        alwaysSelectedIds: {
            // IDs (not full objects) of items that WILL ALWAYS be selected
            // they will be included in preselected, and cannot be unselected
            type: Array,
            default: () => [],
        },
        noShadow: {  // removes shadow from table card
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            initialLoading: true,  // set to false after getting list the first time
            loading: false,  // loading signal for fetching list
            loadingInvite: false,  // busy flag for sending invite to a contact
            totalRows: null,  // total items in list... need to set it after we get items from API, and also after filtering is applied with @filtered event
            itemList: [],   // list from API
            fields: [
                {
                    key: 'full_name',
                    label: 'Name',
                    sortable: true,
                    api_ordering_name: 'lname',  // override this in our custom method to set fname, lname 
                    tdClass: ['font-weight-bold'],
                },{
                    key: 'email',
                    label: 'Email Address',
                    sortable: true,
                },{
                    key: 'job_title',
                    label: 'Title',
                },{
                    key: 'work_location',
                    label: 'Work Location',
                },{
                    // the TenantRoles the TU holds: [{id, slug, name, description}] - badges show the name (Oct 2026, was default_contact_for)
                    key: 'tenant_roles',
                    label: 'Contact Type(s)',
                },{
                    key: 'portal_access',
                    label: 'Status',
                    class: "text-center"
                },{
                    key: 'actions',
                    label: 'Actions',
                    class: 'text-center'
                }
            ],
            messageModal: false,  // v-model to control the message modal
            message: '',  // message to show in messageModal
            //selectedRows: [],  // if we're using selectable, the selected rows go here, full objects
            selectedRowIds: [],  // just the IDs of the selected rows -- much easier to work with only the IDs, and that's all we need
        }
    },
    computed: {
        fieldsToShow: function() {
            // hide any fields specified in hideFields prop
            const shownFields = [ ...this.fields ];
            const selectColumn = {
                key: 'selectColumn',  // so it doesn't collide with backend field names, use pascal case
                label: '',
                sortable: false,
            };
            if (this.selectable) {
                shownFields.unshift(selectColumn);
            }
            if (!this.hideFields || !this.hideFields.length) {
                return shownFields; // this.fields;  // if we didn't specify hiding any fields, just return all of them
            }
            return shownFields.filter(field => !this.hideFields.includes(field.key));  // otherwise only show ones not in hideFields
        },
        isTableSelectable() {
            return !!this.selectable;
        },
        tableSelectMode() {
            return this.selectable ? this.selectable : 'multi';  // this makes multi the default, which it is anyways
        }
    },
    filters: {
        dateFilter: function(value) {
            return FormatDateHelper(value);
        },
    },
    created: function() {
    },
    mounted: function () {
        this.getList();
    },
    methods: {
        // PUBLIC METHOD YOU CAN CALL FROM PARENT TO REFRESH LIST, likely just as $refs.something.refreshList();
        refreshList: function() {
            this.getList();
        },
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        // methods to create/update companies using child CompanyCreateUpdateModal (removed from this component)
        handleUpdate: function(updateObj) {
            this.$refs.companyUserForm.startUpdate(updateObj);
        },
        handleAdd: function() { // only used if list is empty and user clicks on 'add one' button
            //this.$refs.contactModal.startCreate();
            this.$refs.companyUserForm.startCreate();
        },
        getList: function() {  
            this.loading = true;
            // capture any rows already selected so we preserve it after the refresh
            let selectedIdsBeforeLoad = [];
            if (this.initialLoading) {
                // do anything needed on the FISRT load (like select preselectedIds, alwaysSelectedIds)
                selectedIdsBeforeLoad = [ ...this.preselectedIds, ...this.alwaysSelectedIds];
            } else {
                // if not initial load... get from this.selectedRowIds
                selectedIdsBeforeLoad = this.selectedRowIds;  // this.selectedRows.map(item => item.id);
            }
            return suppliosApi.portal.getCompanyUserList()
                .then(response => {
                    //this.totalRows = response.data.count;
                    this.itemList = Array.isArray(response.data) ? response.data.map(item => {
                        return {
                            ...item,
                            portalAccessParams: getPortalAccessParams(item.portal_access),
                        }
                    }) : [];
                    //this.$emit('list-loaded', this.totalRows);  // emit total count to parent
                    console.log("Got itemList from getList function");
                    console.log(this.itemList);
                    return true;
                })
                .catch(error => {
                    this.handleError(error, 'Error while loading itemList');
                    return false;
                })
                .then(success => {
                    console.log('hello from last then of getList');
                    if (this.selectable && selectedIdsBeforeLoad.length) {
                        // just run through all items, if its ID is in preselectedIds, programmatically select it
                        // now we will programmatically select those -- that will then trigger our event handler to handle selections, so we don't call that ourselves
                        // WARNING - if select-mode is 'single' then only the last one will be selected if you passed in multiple preselectedIds!
                        const theTable = this.$refs['companyTuTable'];
                        if (theTable) {
                            this.itemList.forEach((item, index) => {
                                if (selectedIdsBeforeLoad.includes(item.id)) {
                                    theTable.selectRow(index);
                                }
                            });
                        } else {
                            console.log('No table component to handle preselectedIds');
                        }
                    }
                    this.initialLoading = false;  // set flag to say we already loaded once
                    this.loading = false;
                    return success;
                });
        },
        handleSaveComplete: function(tuData) {
            // tuData is passed up from form... will likely only have fname, lname, _email, id, is_company_admin, job_title, work_location
            // UPDATE April 4 2022 - no need to show a toast message... form will already show a success message
            // no full_name -- so we'll check what we have, build up local full name if needed
            // const displayName = tuData.full_name ? tuData.full_name : `${tuData.fname} ${tuData.lname}`;
            // maybe also fall back to _email?
            // this.$toast.success(`Invitation sent to ${displayName}`);
            // IF TABLE IS SELECTABLE NOW, we'll automatically add this new TU as selected - just push their ID into this.selectedRowIds
            this.selectedRowIds.push(tuData.id);  // once its here, it should stay even when we refresh the list
            this.getList();  // refresh the list
        },
        canSendReminder: function(tuObj) {
            // check tuObj.portal_access to see if they have open_invite or expired_invite
            let portal_access = tuObj.portal_access;
            return ['open_invite', 'expired_invite', 'invite_ready'].includes(portal_access);
        },
        sendReminder: function(tuObj) {
            this.message = 'Sending Reminder...';
            this.messageModal = true;
            return suppliosApi.portal.companyUserReminder(tuObj.id)
                .then(() => {
                    this.$toast.success('Reminder sent');
                    this.refreshList();
                    return true;
                })
                .catch(error => {
                    this.handleError(error, 'Error while sending reminder');
                    return false;
                })
                .then(success => {
                    this.messageModal = false;
                    return success;
                });
        },
        removeAccess: function(tuObj) {
            let msg = `Are you sure you want to remove login access for ${tuObj.full_name}? Use this if this person is still an employee, but they should no longer have login access to the supplier portal.`;
            return this.$bvModal.msgBoxConfirm(msg, {
                    title: 'Remove Access?',
                    size: 'sm',
                    buttonSize: 'sm',
                    okVariant: 'danger',
                    okTitle: 'Yes, Remove Access',
                    cancelTitle: 'No, Cancel',
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true,
                })
                .then(value => {
                    // value will be true (OK clicked), false (cancel clicked) or null (some other way of canceling)
                    if (value) {
                        this.performRemoveAccess(tuObj);
                    }
                })
                .catch(err => this.handleError(err, 'Error while removing access'));
        },
        performRemoveAccess: function(tuObj) {
            this.loading = true;
            return suppliosApi.portal.companyUserRemoveAccess(tuObj.id)
                .then(() => {
                    this.$toast.success('Access Removed');
                    this.getList();
                    return true;
                })
                .catch(error => {
                    this.handleError(error, 'Error while removing access');
                    return false;
                })
                .then(success => {
                    this.loading = false;
                    return success;
                });
        },
        deactivateUser: function(tuObj) {
            let msg = `Are you sure you want to permanently delete ${tuObj.full_name}?  Only use this if the user is no longer part of your company.`;
            return this.$bvModal.msgBoxConfirm(msg, {
                    title: 'Delete User?',
                    size: 'sm',
                    buttonSize: 'sm',
                    okVariant: 'danger',
                    okTitle: 'Yes, Delete',
                    cancelTitle: 'No, Cancel',
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true,
                })
                .then(value => {
                    // value will be true (OK clicked), false (cancel clicked) or null (some other way of canceling)
                    if (value) {
                        this.performDeactivateUser(tuObj);
                    }
                })
                .catch(err => this.handleError(err, 'Error while deleting user'));
        },
        performDeactivateUser: function(tuObj) {
            this.loading = true;
            return suppliosApi.portal.companyUserDeactivate(tuObj.id)
                .then(() => {
                    this.$toast.success('Deleted User');
                    this.getList();
                    return true;
                })
                .catch(error => {
                    this.handleError(error, 'Error while deactivating user');
                    return false;
                })
                .then(success => {
                    this.loading = false;
                    return success;
                });
        },
        handleRowSelected(items) {  // items will be array of full data objects from selected rows
            this.selectedRowIds = items.map(item => item.id);
            this.$emit('row-selected', this.selectedRowIds);
        },

    }
}
</script>

<style>

</style>
